/** 价格页 / 支付弹窗埋点（见 docs/pricing-analytics.md） */

import { BILLING_MODES } from '~/constants/pricingPlans.js'

export const PAY_PLAN_CLICK_STORAGE_KEY = 'lip_pay_plan_click'

const TIER_LABELS = {
  starter: 'Starter',
  pro: 'Pro',
  ultra: 'Ultra',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  ultra_ot: 'Ultra',
}

const BILLING_LABELS = {
  [BILLING_MODES.yearly]: 'Yearly',
  [BILLING_MODES.monthly]: 'Monthly',
  [BILLING_MODES.onetime]: 'One-Time',
}

/** @type {{ trigger_type?: string, from?: string, trigger_url?: string, trigger_route?: string, openedAt?: number } | null} */
let stagedPayDialogContext = null

export function resolvePricingUserType(userStore) {
  if (!userStore?.token) return 'anonymous'
  return userStore.has_paid_credits ? 'paid' : 'free'
}

export function buildPlanDisplayName(tier, billing, credits) {
  const tierLabel = TIER_LABELS[tier] ?? String(tier || '')
  const creditsFmt = Number(credits || 0).toLocaleString()
  if (billing === BILLING_MODES.onetime) {
    return `${tierLabel} ${creditsFmt} Credits (One-Time)`
  }
  const billingLabel = BILLING_LABELS[billing] ?? billing
  return `${tierLabel} ${creditsFmt} Credits/mo (${billingLabel})`
}

/** @param {Record<string, unknown> | null | undefined} plan */
export function buildAnalyticsPlanPayload(plan) {
  if (!plan) return null
  const tier = plan.tier ?? ''
  const billing = plan.billing ?? ''
  const credits = Number(plan.credits) || 0
  return {
    sku_key: String(plan.key ?? plan.skuKey ?? ''),
    price_code: String(plan.price_code ?? ''),
    billing,
    tier,
    slider_index: plan.sliderIndex ?? plan.slider_index ?? null,
    plan_name: buildPlanDisplayName(tier, billing, credits),
    credits,
    price_usd: Number(plan.priceUsd ?? plan.price_usd) || 0,
  }
}

export function resolvePayPlanSurface(parentPage) {
  return parentPage === 'pricing' ? 'pricing_page' : 'pay_dialog'
}

export function stagePayDialogOpen({ trigger_type = 'click', from = '' } = {}) {
  if (!import.meta.client) return
  stagedPayDialogContext = {
    trigger_type,
    from,
    trigger_url: window.location.href,
    trigger_route: window.location.pathname,
    openedAt: Date.now(),
  }
}

/** @returns {typeof stagedPayDialogContext} */
export function consumePayDialogOpenContext() {
  const ctx = stagedPayDialogContext
  stagedPayDialogContext = null
  if (ctx) return ctx
  if (!import.meta.client) return { trigger_type: 'click', from: 'unknown' }
  return {
    trigger_type: 'click',
    from: 'unknown',
    trigger_url: window.location.href,
    trigger_route: window.location.pathname,
  }
}

/** @returns {'login' | 'pay'} */
export function openPayDialog(userStore, { triggerType = 'click', from = '' } = {}) {
  stagePayDialogOpen({ trigger_type: triggerType, from })
  if (!userStore?.token) {
    userStore.setLoginModalShow(true)
    return 'login'
  }
  userStore.setPriceModalShow(true)
  return 'pay'
}

export function persistPayPlanClickContext(payload) {
  if (!import.meta.client) return
  const serialized = JSON.stringify({ ...payload, savedAt: Date.now() })
  try {
    // localStorage：收银台在新标签页打开时，回跳页需跨 tab 读取最近一次点击的套餐
    localStorage.setItem(PAY_PLAN_CLICK_STORAGE_KEY, serialized)
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(PAY_PLAN_CLICK_STORAGE_KEY, serialized)
  } catch {
    /* ignore */
  }
}

export function readPayPlanClickContext() {
  if (!import.meta.client) return null
  try {
    const raw =
      localStorage.getItem(PAY_PLAN_CLICK_STORAGE_KEY) ||
      sessionStorage.getItem(PAY_PLAN_CLICK_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function clearPayPlanClickContext() {
  if (!import.meta.client) return
  try {
    localStorage.removeItem(PAY_PLAN_CLICK_STORAGE_KEY)
    sessionStorage.removeItem(PAY_PLAN_CLICK_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}
